import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Box,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import useAPI from "../../../../api/hooks/useAPI";
import useIsIntegrated from "../../../../api/hooks/useIsIntegrated";
import { integrationKeys } from "../../../../api/queryKeys";
import PrimaryButton from "../../../../components/buttons/PrimaryButton";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";
import CustomCopyButton from "../../../../components/CustomCopyButton";
import TextWithExternalLink from "../../../../components/ExternalLink";
import useExpandedList from "../../../../components/hooks/ExpandedList";
import LoadingIcon from "../../../../components/LoadingIcon";
import { ScreenshotAccordion } from "../../../../components/ScreenshotAccordion";
import { Toast } from "../../../../components/Toast";
import { logos } from "../../environment/icons/iconExport";
import Guideline from "../components/Guideline";
import GuidelineStep from "../components/GuidelineStep";
import GuidelineStepByStep from "../components/GuidelineStepByStep";
import IntegrationGuideAlert from "../components/IntegrationGuideAlert";
import copyTokenAndOrgId from "./images/copy_token_org_id.png";
import goToApiPage from "./images/go_to_create_api_token.png";
import createApiToken from "./images/name_date_expires.png";

import selectApiKeyAccessType from "./images/type_api_token.png";

const REVOKE_USER_ACCESS_API_URL =
  "https://developer.atlassian.com/cloud/admin/organization/rest/api-group-directory/#api-v1-orgs-orgid-users-userid-roles-revoke-post";

const AtlassianIntegration = () => {
  const { isIntegrated, isIntegratedLoading } = useIsIntegrated("atlassian");
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const [guideMode, setGuideMode] = useState(false);
  const [apiKey, setApiKey] = useState("");
  const [orgId, setOrgId] = useState("");
  const [error, setError] = useState("");
  const [showApiKey, setShowApiKey] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (searchParams.has("viewonly")) {
      setGuideMode(true);
    }
  }, [searchParams]);

  const { addToExpandedList, handleExpandAll, handleCollapseAll } =
    useExpandedList();
  const { registerAtlassianIntegration } = useAPI();

  const handleOrgIdChange = (e) => {
    setOrgId(e.target.value);
    setError("");
  };

  const handleApiKeyChange = (e) => {
    setApiKey(e.target.value);
    setError("");
  };

  const handleClickShowApiKey = () => {
    setShowApiKey(!showApiKey);
  };

  const handleMouseDownShowApiKey = (event) => {
    event.preventDefault();
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!apiKey) {
      setError("API Key is required");
      return;
    }
    if (!orgId) {
      setError("Organization ID is required");
      return;
    }

    setError("");
    setLoading(true);
    registerAtlassianIntegration({
      api_key: apiKey,
      org_id: orgId,
    })
      .then((response) => {
        queryClient.invalidateQueries({
          queryKey: integrationKeys.all,
        });
        Toast.success("Atlassian integration successful");
        setApiKey("");
        setOrgId("");
      })
      .catch((error) => {
        if (error.response) {
          Toast.error(`Something went wrong: ${error.response.data.detail}`);
        } else {
          Toast.error("An error occurred while processing the request.");
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Box>
      <Guideline
        title={
          !isIntegrated
            ? "Once integrated, the Breeze platform will automatically sync with Atlassian to enhance your workflow management."
            : ""
        }
        image={logos.AtlassianLogo}
        name={"Atlassian"}
        content={
          loading || isIntegratedLoading ? (
            <LoadingIcon size={25} />
          ) : isIntegrated && !guideMode ? (
            <Box>
              <Typography variant={"h4"}>Atlassian is integrated!</Typography>
            </Box>
          ) : (
            <div>
              {/* BUTTONS TO CONTROL EXPANSION */}
              <Box mb="20px">
                <Grid container spacing={2} direction={"row"}>
                  {guideMode && <IntegrationGuideAlert />}
                  <Grid item>
                    <SecondaryButton onClick={handleExpandAll}>
                      Expand All Screenshots
                    </SecondaryButton>
                  </Grid>
                  <Grid item>
                    <SecondaryButton onClick={handleCollapseAll}>
                      Collapse All Screenshots
                    </SecondaryButton>
                  </Grid>
                </Grid>
              </Box>
              {/* SETUP INSTRUCTIONS */}
              <Box component="form" onSubmit={handleSubmit}>
                <GuidelineStepByStep>
                  <GuidelineStep title="Access the Admin Dashboard">
                    <Typography variant={"body2"}>
                      Login to your Atlassian account with Organization Admin
                      privileges.
                    </Typography>
                    <TextWithExternalLink
                      text="Navigate to the "
                      linkText="Atlassian Admin Dashboard"
                      href="https://admin.atlassian.com/"
                    />
                    <Typography variant={"body2"}>
                      From the top menu, navigate to Settings. Then, in the left
                      menu, click on "API keys."
                    </Typography>
                    <Typography variant={"body2"}>
                      Click the "Create API key" button to start the process.
                    </Typography>
                    <ScreenshotAccordion
                      images={[
                        {
                          file: goToApiPage,
                          altext: "Create API Token",
                        },
                      ]}
                      onExpanded={addToExpandedList}
                    />
                  </GuidelineStep>
                  <GuidelineStep title="Select API key access type">
                    <Typography variant={"body2"}>
                      Select <b>"API key without scopes"</b> to enable User
                      collector and additional gaps detection, including
                      remediation capabilities.
                    </Typography>
                    <Typography
                      variant={"body2"}
                      sx={{ mt: 1, fontStyle: "italic" }}
                    >
                      Note: Scoped tokens currently only provide limited
                      functionality and analysis.
                    </Typography>
                    <ScreenshotAccordion
                      images={[
                        {
                          file: selectApiKeyAccessType,
                          altext: "Create API Token",
                        },
                      ]}
                      onExpanded={addToExpandedList}
                    />
                  </GuidelineStep>

                  <GuidelineStep title="Name your API key">
                    <Box display="flex" gap={1} alignItems="center">
                      <Typography variant={"body2"}>
                        Give your API key a descriptive name, such as
                        "Breeze-Token".
                      </Typography>
                      <CustomCopyButton
                        copyTitle="Name"
                        copyText="Breeze-Token"
                      />
                    </Box>
                    <Typography variant={"body2"}>
                      Select an expiration date of one year.
                    </Typography>
                    <Typography variant={"body2"} sx={{ mt: 1 }}>
                      Click "Next" to continue to the scopes selection.
                    </Typography>
                    <ScreenshotAccordion
                      images={[
                        {
                          file: createApiToken,
                          altext: "Create API Token",
                        },
                      ]}
                      onExpanded={addToExpandedList}
                    />
                  </GuidelineStep>

                  <GuidelineStep title="Create your API key">
                    <Typography variant={"body2"}>
                      Since you selected "API key without scopes", you can
                      proceed directly to create your API key.
                    </Typography>
                    <Typography variant={"body2"} sx={{ mt: 1 }}>
                      Click "Create API key" to generate your key.
                    </Typography>
                  </GuidelineStep>

                  <GuidelineStep title="Copy your API Key and Organization ID">
                    <Typography variant={"body2"}>
                      After creating the API key, you'll be presented with two
                      values:
                    </Typography>
                    <ol>
                      <li>
                        <Typography
                          variant={"body2"}
                          sx={{ fontWeight: "medium" }}
                        >
                          Organization ID: This appears in a field labeled
                          "Organization ID"
                        </Typography>
                      </li>
                      <li>
                        <Typography
                          variant={"body2"}
                          sx={{ fontWeight: "medium" }}
                        >
                          API Key: The long string that appears below the
                          Organization ID
                        </Typography>
                      </li>
                    </ol>

                    <ScreenshotAccordion
                      images={[
                        {
                          file: copyTokenAndOrgId,
                          altext: "Copy Token and Organization ID",
                        },
                      ]}
                      onExpanded={addToExpandedList}
                    />
                  </GuidelineStep>

                  <GuidelineStep title="Enter your credentials">
                    <Typography variant={"body2"}>
                      Enter your API key and Organization ID below.
                    </Typography>

                    <Box width={700} sx={{ mt: 2 }}>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle1">
                          Organization ID
                        </Typography>
                        <TextField
                          placeholder="Your Atlassian Organization ID"
                          fullWidth
                          value={orgId}
                          onChange={handleOrgIdChange}
                          disabled={guideMode}
                        />
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle1">API Key</Typography>
                        <TextField
                          placeholder="Your Atlassian Admin API key"
                          fullWidth
                          type={showApiKey ? "text" : "password"}
                          value={apiKey}
                          onChange={handleApiKeyChange}
                          disabled={guideMode}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  aria-label="toggle api key visibility"
                                  onClick={handleClickShowApiKey}
                                  onMouseDown={handleMouseDownShowApiKey}
                                >
                                  {showApiKey ? (
                                    <Visibility />
                                  ) : (
                                    <VisibilityOff />
                                  )}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Box>
                      {error && (
                        <Typography
                          color="error"
                          variant="body2"
                          sx={{ mt: 2 }}
                        >
                          {error}
                        </Typography>
                      )}
                    </Box>
                  </GuidelineStep>

                  <GuidelineStep title='All done, now hit "Connect" to integrate!'>
                    <Box display="flex" justifyContent="center">
                      <PrimaryButton
                        type="submit"
                        disabled={guideMode || !apiKey || !orgId || loading}
                      >
                        Connect
                      </PrimaryButton>
                    </Box>
                  </GuidelineStep>
                </GuidelineStepByStep>
              </Box>
            </div>
          )
        }
      />
    </Box>
  );
};

export default AtlassianIntegration;
